import {useEffect, useMemo, useState} from "react";
import dayjs from "dayjs";
import {useParams, useHistory, useLocation} from "react-router-dom";
import {useSelector} from "react-redux";
import {DashboardLayout} from "components/Layout";
import {BasicTable, SearchBox} from "components/Table";
import {DatePicker} from "components/DatePicker";
import {BasicDropdown} from "components/Dropdown";
import {useUserAttendance} from "hooks/AdminHospital/Attendance/useQueryAttendanceUser";

const formatDurationSeconds = seconds => {
	if (!Number.isFinite(seconds) || seconds <= 0) return "-";

	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const hh = String(hours).padStart(2, "0");
	const mm = String(minutes).padStart(2, "0");

	return `${hh} jam ${mm} menit`;
};

export function UserAttendanceDetailAdminHospitalPage() {
	const {userId} = useParams();
	const history = useHistory();
	const location = useLocation();
	const selectedHospital = useSelector(state => state.HospitalsReducer.selectedHospital);
	const hospitalId = selectedHospital?.Hospital?.id;

	const [dateRange, setDateRange] = useState({startDate: undefined, endDate: undefined});
	const [dateDisplay, setDateDisplay] = useState("");
	const [dataLimit, setDataLimit] = useState(10);

	const {data, setFilter, isLoading} = useUserAttendance(hospitalId, userId);

	// Initialize filters from list page state (startDate, endDate, search)
	useEffect(() => {
		const initStart = location?.state?.startDate;
		const initEnd = location?.state?.endDate;
		const initSearch = location?.state?.search;

		if (initStart && initEnd) {
			setDateRange({startDate: initStart, endDate: initEnd});
			setDateDisplay(`${dayjs(initStart).format("MM/DD/YYYY")} - ${dayjs(initEnd).format("MM/DD/YYYY")}`);
		}

		if (typeof initSearch === "string") {
			setFilter(prev => ({...prev, search: initSearch}));
		}
	}, [location?.state, setFilter]);

	useEffect(() => {
		setFilter(prev => ({
			...prev,
			hospitalId: hospitalId || null,
			date: undefined,
			startDate: dateRange.startDate,
			endDate: dateRange.endDate,
			page: 1,
		}));
	}, [hospitalId, dateRange, setFilter]);

	const rows = (data?.data?.rows || []).filter(r => String(r?.userId ?? "") === String(userId ?? ""));

	const columns = useMemo(
		() => [
			{Header: "No", accessor: "idSerial", width: 60, centered: true},
			{Header: "Tanggal", accessor: "date", centered: true},
			{Header: "Jam Masuk", accessor: "checkIn", centered: true},
			{Header: "Jam Pulang", accessor: "checkOut", centered: true},
			{Header: "Durasi Kerja", accessor: "workDuration", centered: true},
			{Header: "Status", accessor: "status", centered: true},
		],
		[],
	);

	const displayRows = useMemo(() => {
		return rows.map((r, idx) => ({
			...r,
			idSerial: r.idSerial ?? idx + 1,
			date: r.date || (r.checkInDate ? dayjs(r.checkInDate).format("YYYY-MM-DD") : "-"),
			workDuration: (() => {
				const checkInAt = r?.checkInAt;
				const checkOutAt = r?.checkOutAt;

				if (!checkInAt || !checkOutAt) return "-";

				const start = dayjs(checkInAt);
				const end = dayjs(checkOutAt);

				if (!start.isValid() || !end.isValid()) return "-";

				const diffSeconds = end.diff(start, "second");

				return formatDurationSeconds(diffSeconds);
			})(),
		}));
	}, [rows]);

	const userDurationSeconds = data?.data?.summary?.userDurationSeconds ?? 0;

	const totalDurationDisplay = useMemo(() => {
		const hours = Math.floor(userDurationSeconds / 3600);
		const minutes = Math.floor((userDurationSeconds % 3600) / 60);
		const hh = String(hours).padStart(2, "0");
		const mm = String(minutes).padStart(2, "0");

		return `${hh} jam ${mm} menit`;
	}, [userDurationSeconds]);

	const handleBack = () => history.push("/hospital/absensi-staff");

	return (
		<DashboardLayout onBackButtonClick={handleBack}>
			<>
				<div class="text-bold text-dark mb-4">
					<a class="text-dark" style={{cursor: "pointer"}} onClick={handleBack}>
						<span class="icon-ico-back me-2" />
					</a>
					Detail Absensi Staff : {displayRows?.[0]?.staffName || "-"}
				</div>
				<SearchBox
					bottomElement={
						<p class="mb-0 ms-1">
							Total Durasi Absensi: <strong>{totalDurationDisplay}</strong>
						</p>
					}
					rightElement={
						<div className="d-flex align-items-end gap-2">
							<div style={{minWidth: "220px"}}>
								<label class="setting-label">Rentang Waktu</label>
								<BasicDropdown
									data={[
										{label: "1 Minggu Lalu", value: "1w"},
										{label: "1 Bulan Lalu", value: "1m"},
										{label: "1 Tahun Lalu", value: "1y"},
									]}
									placeholder="Pilih rentang"
									onSelect={val => {
										const now = dayjs();
										let start;

										if (val === "1w") start = now.subtract(1, "week");
										else if (val === "1m") start = now.subtract(1, "month");
										else start = now.subtract(1, "year");

										const startDate = start.format("YYYY-MM-DD");
										const endDate = now.format("YYYY-MM-DD");

										setDateRange({startDate, endDate});
										setDateDisplay(`${start.format("MM/DD/YYYY")} - ${now.format("MM/DD/YYYY")}`);
									}}
								/>
							</div>
							<div style={{minWidth: "220px"}}>
								<label class="setting-label">Tanggal</label>
								<DatePicker
									initialSettings={{autoUpdateInput: false}}
									singleDatePicker={false}
									onChange={val => {
										const {startDate, endDate} = val || {};

										if (startDate && endDate) {
											setDateRange({startDate, endDate});
											setDateDisplay(
												`${dayjs(startDate).format("MM/DD/YYYY")} - ${dayjs(endDate).format(
													"MM/DD/YYYY",
												)}`,
											);
										}
									}}>
									<input
										class="form-control"
										placeholder="Pilih rentang tanggal"
										value={dateDisplay}
									/>
								</DatePicker>
							</div>
						</div>
					}
					onSearch={keyword => {
						setFilter(prev => ({...prev, search: keyword || "", page: 1}));
					}}
				/>

				{!hospitalId ? (
					<div className="p-4 text-center text-muted">Rumah sakit belum dipilih.</div>
				) : (
					<BasicTable
						columns={columns}
						currentPage={data?.data?.currentPage || 1}
						data={displayRows}
						hidePagination={true}
						limit={dataLimit}
						loading={isLoading}
						striped={true}
						title="Daftar Absensi"
						totalItems={data?.data?.totalItems ?? rows.length}
						totalPage={1}
						onLimitChange={limit => {
							setDataLimit(limit);
							setFilter(prev => ({...prev, limit}));
						}}
						onPageChange={() => {}}
					/>
				)}
			</>
		</DashboardLayout>
	);
}

export default UserAttendanceDetailAdminHospitalPage;
